<script lang="ts" setup>
import { ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import api from '@/api/index';
import SettingToggleCard from '@/components/common/atoms/setting-toggle-card.vue';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import Content from '@/components/layouts/content.vue';
import Header from '@/components/layouts/header.vue';
import { useUserSettingsNews } from '@/utils/swr';

const route = useRoute();
const { createSnackbar } = useSnackbar();

const { data: userSettings, mutate } = useUserSettingsNews();

const enableTranslateArticleTitle = ref(false);
const enableHideLoginRequiredArticle = ref(false);

watch(
  userSettings,
  settings => {
    if (!settings) return;
    enableTranslateArticleTitle.value = settings.enable_translate_article_title;
    enableHideLoginRequiredArticle.value =
      settings.enable_hide_login_required_article;
  },
  { immediate: true },
);

const submit = async () => {
  if (!userSettings.value) return;

  try {
    const params = {
      ...userSettings.value,
      enable_translate_article_title: enableTranslateArticleTitle.value,
      enable_hide_login_required_article: enableHideLoginRequiredArticle.value,
    };

    await api.updateUserSettingsNews(params, {
      pageName: 'news',
      pageUrl: route.fullPath,
    });
    mutate();
    createSnackbar({
      message: '設定を更新しました',
      type: 'success',
    });
  } catch (e) {
    createSnackbar({
      message: '設定を更新できませんでした',
      type: 'error',
    });
    throw e;
  }
};
</script>
<template>
  <div class="user-settings" v-if="userSettings">
    <Header title="ニュース配信"></Header>
    <Content>
      <div class="settings-card">
        <div class="c-formBlock">
          <SettingToggleCard
            title="英語記事のタイトル翻訳"
            v-model="enableTranslateArticleTitle"
          >
            海外メディア記事のタイトルが翻訳された形で表示されます。
          </SettingToggleCard>
          <SettingToggleCard
            title="要ログイン記事の非表示"
            v-model="enableHideLoginRequiredArticle"
          >
            ログインの必要な記事が表示されなくなります。
          </SettingToggleCard>
        </div>
      </div>
    </Content>
    <div class="settings-footer">
      <button
        class="c-btn c-btn--small c-btn--auto c-btn--AnewsPrimary"
        @click="submit"
      >
        保存
      </button>
    </div>
  </div>
</template>
<style lang="scss" scoped>
// 内容が短くても画面の最下部に置く
.settings-footer {
  position: sticky;
  bottom: 0;
  margin-top: auto;
  padding: 16px 24px;
  background: $color-white;
  box-shadow: 0px 1px 5px rgba($color-gray1000, 0.25);
}

.user-settings {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100%;
  // コンテンツを内容領域いっぱいに広げる（Header・Contentの幅もこの変数で決まる）
  --layout-content-width: 100%;

  // 共通部品の SettingToggleCard は変えず、行の高さをデザイン（16px）に揃える
  :deep(.m-setting-title) {
    line-height: 16px;
  }

  .settings-card {
    box-sizing: border-box;
    max-width: 1248px;
    margin: 0 24px 24px;
    padding: 16px;
    background: #fff;
    border: 1px solid $color-border;
    border-radius: $border-radius;

    .c-formBlock {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
  }
}
</style>
